import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Refresh, Database, Activity, AlertTriangle } from "@/components/icons";

import { fetchCacheStats } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Stat } from "@/components/Stat";
import { Card, CardContent, CardTitle } from "@/components/ui/card";

function fmtInt(n: number): string {
  return n.toLocaleString();
}

function backendBadge(backend: string): { label: string; cls: string } {
  switch (backend) {
    case "redis":
      return { label: "Redis", cls: "bg-success/20 text-success" };
    case "memory":
      return { label: "In-process memory", cls: "bg-warning/20 text-warning" };
    default:
      return { label: "Disabled", cls: "bg-error/20 text-error" };
  }
}

export function CacheStatsPage() {
  const statsQ = useQuery({
    queryKey: ["cache", "stats"],
    queryFn: fetchCacheStats,
    refetchInterval: 5_000,
  });

  const data = statsQ.data;
  const total = data ? data.hits + data.misses : 0;
  const hitPct = data ? data.hit_rate * 100 : 0;
  const missPct = total ? (data!.misses / total) * 100 : 0;
  const badge = data ? backendBadge(data.backend) : null;

  // A low hit rate under meaningful traffic is the signal worth flagging:
  // build_fn() (incl. the 23k-unit sweep behind /api/units) is running often.
  const lowHitRate = !!data && data.enabled && total > 200 && data.hit_rate < 0.9;

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Response Cache</h1>
          <div className="text-sm opacity-70">
            In-process JSON response cache (hit/miss counters) — high hit rate means repeat
            API requests are absorbed before rebuilding the payload.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {badge && (
            <span className={`rounded-full px-3 py-1 text-xs font-medium ${badge.cls}`}>
              {badge.label}
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={() => statsQ.refetch()}>
            <Refresh className="h-4 w-4" /> Refresh
          </Button>
        </div>
      </div>

      {statsQ.isError ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          Failed to load cache stats
        </div>
      ) : (
        <>
          {lowHitRate && (
            <div className="flex items-start gap-2 rounded-md border border-warning/50 bg-warning/10 p-3 text-sm text-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Hit rate is below 90% under load — build_fn() is running often and contending
                with UDP workers for the GIL. Consider raising the cache TTL or moving the
                backend to Redis.
              </span>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Stat
              title="Hit Rate"
              value={`${hitPct.toFixed(1)}%`}
              icon={<Activity className="h-4 w-4 opacity-60" />}
              description={`${fmtInt(total)} cacheable requests`}
            />
            <Stat title="Hits" value={fmtInt(data?.hits ?? 0)} />
            <Stat title="Misses" value={fmtInt(data?.misses ?? 0)} />
            <Stat
              title="Bypassed"
              value={fmtInt(data?.bypassed ?? 0)}
              description="not cacheable / cache off"
            />
          </div>

          <Card>
            <CardContent className="space-y-4 py-5">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Database className="h-4 w-4" /> Hit / Miss distribution
              </CardTitle>

              {total === 0 ? (
                <div className="text-sm opacity-60">No cacheable requests recorded yet.</div>
              ) : (
                <>
                  <div className="flex h-6 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="flex items-center justify-center bg-success/70 text-[10px] font-medium text-success-content"
                      style={{ width: `${hitPct}%` }}
                      title={`Hits: ${fmtInt(data!.hits)} (${hitPct.toFixed(1)}%)`}
                    >
                      {hitPct >= 12 ? `${hitPct.toFixed(0)}%` : ""}
                    </div>
                    <div
                      className="flex items-center justify-center bg-error/60 text-[10px] font-medium text-error-content"
                      style={{ width: `${missPct}%` }}
                      title={`Misses: ${fmtInt(data!.misses)} (${missPct.toFixed(1)}%)`}
                    >
                      {missPct >= 12 ? `${missPct.toFixed(0)}%` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs opacity-70">
                    <span className="flex items-center gap-1">
                      <span className="h-3 w-3 rounded bg-success/70" /> Hits
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="h-3 w-3 rounded bg-error/60" /> Misses
                    </span>
                  </div>
                </>
              )}

              <dl className="grid grid-cols-2 gap-x-8 gap-y-2 border-t border-border pt-4 text-sm sm:grid-cols-3">
                <div>
                  <dt className="opacity-60">Enabled</dt>
                  <dd className="font-mono">{data ? String(data.enabled) : "—"}</dd>
                </div>
                <div>
                  <dt className="opacity-60">Backend</dt>
                  <dd className="font-mono">{data?.backend ?? "—"}</dd>
                </div>
                <div>
                  <dt className="opacity-60">Auto-refresh</dt>
                  <dd className="font-mono">5s</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
